// Detalle de un negocio con pestañas Zonas / Tiendas / Usuarios / Jerarquía — ejerce todos los
// endpoints de sub-entidades de retail-org: listar, crear, EDITAR, cambiar estado, transferir
// (usuario), ver-por-id (tienda/usuario) y jerarquía. ⚠️ Beyond-mock: el mock B01 no dibuja este
// detalle; se construye para operar/probar la jerarquía (gap C1). Usa el kit del dominio.
import { useState, type ReactNode } from 'react';
import { Plus } from 'lucide-react';
import { toast } from 'sonner';
import { Badge, Button, EmptyState, Input, Loading, Modal, PhoneInput, Select, Tabs, usePermissions, type SelectOption } from '@/kit';
import { validateForm, type FieldErrors } from '@/lib/formValidators';
import { fromStoredPhone } from '@/lib/phoneMx';
import { EntityDetailModal } from '../../components';
import { EmploymentTypeEnum } from '@fiado/type-kit/bin/retailOrg/enums/EmploymentTypeEnum';
import { PermissionScope } from '@fiado/type-kit/bin/platformRbac/enums/PermissionScope';
import type { CreateZoneRequest } from '@fiado/type-kit/bin/retailOrg/dtos/requests/CreateZoneRequest';
import type { CreateStoreRequest } from '@fiado/type-kit/bin/retailOrg/dtos/requests/CreateStoreRequest';
import type { ProvisionRetailUserRequest } from '@fiado/type-kit/bin/retailOrg/dtos/requests/ProvisionRetailUserRequest';
import { getStore, getRetailUser } from '@/api/services/retail-org/retail-org.client';
import {
    useZones,
    useCreateZone,
    useUpdateZone,
    useChangeZoneStatus,
    useStores,
    useCreateStore,
    useUpdateStore,
    useChangeStoreStatus,
    useRetailUsers,
    useProvisionRetailUser,
    useUpdateRetailUser,
    useChangeRetailUserStatus,
    useTransferRetailUser,
    useRetailerHierarchy,
    type Retailer,
    type Store,
    type RetailUser,
} from '../api/useRetailOrg';
import { canEditStore, storeToForm } from './storeForm';
import { retailStoreContactSchema, retailUserCreateSchema, retailUserEditSchema } from '../lib/retailerForms';
import { EMPLOYMENT_OPTIONS } from '../lib/employmentOptions';
import { SellerWalletModal } from './SellerWalletModal';
import { WALLET_LINK_PERMISSION, walletBadgeText } from './walletLink';
import { IntegrationLockNotice, IntegrationLockProvider, useIntegrationLock } from '../../admin/integration/components/IntegrationLock';
import { isIntegratedRetailer } from '../../admin/integration/lib/integrationLock';

const ACTIVE = 'ACTIVE';
const INACTIVE = 'INACTIVE';

const STATUS_VARIANT: Record<string, string> = { ACTIVE: 'success', SUSPENDED: 'warning', INACTIVE: 'secondary' };
const STATUS_LABEL: Record<string, string> = { ACTIVE: 'Activo', SUSPENDED: 'Suspendido', INACTIVE: 'Inactivo' };

function StatusBadge({ status }: { status: string }) {
    return <Badge variant={STATUS_VARIANT[status] ?? 'default'}>{STATUS_LABEL[status] ?? status}</Badge>;
}
function errMsg(e: unknown, fallback: string): string {
    return e instanceof Error ? e.message : fallback;
}
const isActive = (status: string): boolean => status === ACTIVE;
const asRecord = (v: unknown): Record<string, unknown> => v as Record<string, unknown>;

const TABS = [
    { key: 'zonas', label: 'Zonas' },
    { key: 'tiendas', label: 'Tiendas' },
    { key: 'usuarios', label: 'Usuarios' },
    { key: 'jerarquia', label: 'Jerarquía' },
] as const;

interface Props {
    retailer: Retailer | null;
    initialTab?: string;
    onClose: () => void;
}

export function RetailerDetailModal({ retailer, initialTab, onClose }: Props) {
    const [tab, setTab] = useState<string>(initialTab ?? 'zonas');
    const retailerId = retailer?.retailerId ?? '';

    return (
        <Modal isOpen={!!retailer} onClose={onClose} title={retailer ? `Administrar — ${retailer.name}` : ''} size="xl">
            <Tabs tabs={TABS} activeTab={tab} onChange={setTab} />
            <IntegrationLockProvider integrated={isIntegratedRetailer(retailer)}>
                <div className="pt-4">
                    {tab === 'zonas' && <ZonasTab retailerId={retailerId} />}
                    {tab === 'tiendas' && <TiendasTab retailerId={retailerId} />}
                    {tab === 'usuarios' && <UsuariosTab retailerId={retailerId} />}
                    {tab === 'jerarquia' && <JerarquiaTab retailerId={retailerId} />}
                </div>
            </IntegrationLockProvider>
        </Modal>
    );
}

// Fila genérica: nombre + meta + estado + acciones.
function Row({
    title,
    meta,
    status,
    actions,
}: {
    title: string;
    meta?: string;
    status: string;
    actions: ReactNode;
}) {
    return (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border bg-card px-4 py-2.5">
            <div className="flex items-center gap-2">
                <span className="text-[13px] font-semibold">{title}</span>
                {meta ? <span className="text-[12px] text-muted-foreground">{meta}</span> : null}
                <StatusBadge status={status} />
            </div>
            <div className="flex items-center gap-2">{actions}</div>
        </div>
    );
}

// ── Zonas ──────────────────────────────────────────────────────────────────

function ZonasTab({ retailerId }: { retailerId: string }) {
    const { data, isLoading } = useZones(retailerId);
    const createZone = useCreateZone(retailerId);
    const updateZone = useUpdateZone(retailerId);
    const changeStatus = useChangeZoneStatus(retailerId);
    const lock = useIntegrationLock();
    const [editingId, setEditingId] = useState<string | null>(null);
    const [form, setForm] = useState({ name: '', region: '' });

    const reset = () => {
        setEditingId(null);
        setForm({ name: '', region: '' });
    };
    const submit = () => {
        const body: CreateZoneRequest = { name: form.name, region: form.region };
        if (editingId) {
            // Nombre no editable (id único) → se omite del update.
            updateZone.mutate(
                { zoneId: editingId, body: { region: form.region } },
                { onSuccess: () => { toast.success('Zona actualizada'); reset(); }, onError: (e) => toast.error(errMsg(e, 'Error')) },
            );
        } else {
            createZone.mutate(body, {
                onSuccess: () => { toast.success('Zona creada'); reset(); },
                onError: (e) => toast.error(errMsg(e, 'No se pudo crear la zona')),
            });
        }
    };

    return (
        <div className="space-y-4">
            <form onSubmit={(e) => { e.preventDefault(); submit(); }} className="flex flex-wrap items-end gap-2">
                <Input label="Nombre" value={form.name} onChange={(e) => { setForm((f) => ({ ...f, name: e.target.value })); }} disabled={!!editingId} hint={editingId ? 'No editable' : undefined} required />
                <Input label="Región" value={form.region} onChange={(e) => { setForm((f) => ({ ...f, region: e.target.value })); }} required />
                <Button type="submit" disabled={lock.locked} title={lock.reason} isLoading={createZone.isPending || updateZone.isPending} leftIcon={editingId ? undefined : <Plus aria-hidden />}>
                    {editingId ? 'Guardar zona' : 'Crear zona'}
                </Button>
                {editingId ? <Button type="button" variant="ghost" onClick={reset}>Cancelar</Button> : null}
            </form>

            {isLoading ? (
                <Loading text="Cargando zonas…" />
            ) : !data || data.length === 0 ? (
                <EmptyState title="Sin zonas" description="Crea la primera zona con el formulario de arriba." />
            ) : (
                <div className="space-y-2">
                    {data.map((z) => (
                        <Row
                            key={z.zoneId}
                            title={z.name}
                            meta={z.region}
                            status={z.status}
                            actions={
                                <>
                                    <Button variant="outline" size="sm" disabled={lock.locked} title={lock.reason} onClick={() => { setEditingId(z.zoneId); setForm({ name: z.name, region: z.region }); }}>Editar</Button>
                                    <Button variant="outline" size="sm" disabled={lock.locked} title={lock.reason} isLoading={changeStatus.isPending && changeStatus.variables.zoneId === z.zoneId} onClick={() => { changeStatus.mutate({ zoneId: z.zoneId, status: isActive(z.status) ? INACTIVE : ACTIVE }, { onError: (e) => toast.error(errMsg(e, 'Error')) }); }}>
                                        {isActive(z.status) ? 'Desactivar' : 'Activar'}
                                    </Button>
                                </>
                            }
                        />
                    ))}
                </div>
            )}
        </div>
    );
}

// ── Tiendas ────────────────────────────────────────────────────────────────

const EMPTY_STORE = { zoneId: '', code: '', name: '', address1: '', city: '', state: '', zipCode: '', lat: '', lng: '', phone: '' };

function TiendasTab({ retailerId }: { retailerId: string }) {
    const { data, isLoading } = useStores(retailerId);
    const { data: zones } = useZones(retailerId);
    const createStore = useCreateStore(retailerId);
    const updateStore = useUpdateStore(retailerId);
    const changeStatus = useChangeStoreStatus(retailerId);
    const lock = useIntegrationLock();
    const [editingId, setEditingId] = useState<string | null>(null);
    const [form, setForm] = useState(EMPTY_STORE);
    const [errors, setErrors] = useState<FieldErrors<'phone' | 'zipCode'>>({});
    const [viewRecord, setViewRecord] = useState<Record<string, unknown> | null>(null);

    const zoneOptions = (zones ?? []).map((z) => ({ value: z.zoneId, label: z.name }));
    const set = (k: keyof typeof EMPTY_STORE) => (v: string) => {
        setForm((f) => ({ ...f, [k]: v }));
        setErrors((e) => ({ ...e, [k]: undefined }));
    };
    const reset = () => { setEditingId(null); setForm(EMPTY_STORE); setErrors({}); };

    const startEdit = (s: Store) => {
        setEditingId(s.storeId);
        setForm(storeToForm(s));
    };

    const submit = () => {
        const checked = validateForm(retailStoreContactSchema, { phone: form.phone, zipCode: form.zipCode });
        if (!checked.success) { setErrors(checked.errors); return; }
        const { phone, zipCode } = checked.data;
        const address = { address1: form.address1, city: form.city, state: form.state, zipCode };
        const location = { lat: Number(form.lat), lng: Number(form.lng) };
        const zonePart = form.zoneId ? { zoneId: form.zoneId } : {};
        if (editingId) {
            // Nombre no editable (id único) → se omite del update.
            updateStore.mutate(
                { storeId: editingId, body: { code: form.code, address, location, phone, ...zonePart } },
                { onSuccess: () => { toast.success('Tienda actualizada'); reset(); }, onError: (e) => toast.error(errMsg(e, 'Error')) },
            );
            return;
        }
        const body = { code: form.code, name: form.name, address, location, phone, ...zonePart } as CreateStoreRequest;
        createStore.mutate(body, {
            onSuccess: () => { toast.success('Tienda creada'); reset(); },
            onError: (e) => toast.error(errMsg(e, 'No se pudo crear la tienda')),
        });
    };

    return (
        <div className="space-y-4">
            <form onSubmit={(e) => { e.preventDefault(); submit(); }} className="space-y-3 rounded-lg border border-border bg-muted/30 p-3">
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                    <Select label="Zona" value={form.zoneId} onChange={set('zoneId')} options={zoneOptions} placeholder="Sin zona (opcional)" />
                    <Input label="Código" value={form.code} onChange={(e) => { set('code')(e.target.value); }} required />
                    <Input label="Nombre" value={form.name} onChange={(e) => { set('name')(e.target.value); }} disabled={!!editingId} hint={editingId ? 'No editable' : undefined} required />
                    <Input label="Calle" value={form.address1} onChange={(e) => { set('address1')(e.target.value); }} required />
                    <Input label="Ciudad" value={form.city} onChange={(e) => { set('city')(e.target.value); }} required />
                    <Input label="Estado" value={form.state} onChange={(e) => { set('state')(e.target.value); }} required />
                    <Input label="C.P." inputMode="numeric" maxLength={5} value={form.zipCode} onChange={(e) => { set('zipCode')(e.target.value); }} error={errors.zipCode} required />
                    <Input label="Lat" type="number" value={form.lat} onChange={(e) => { set('lat')(e.target.value); }} required />
                    <Input label="Lng" type="number" value={form.lng} onChange={(e) => { set('lng')(e.target.value); }} required />
                    <PhoneInput label="Teléfono" value={form.phone} onValueChange={set('phone')} error={errors.phone} required />
                </div>
                <div className="flex justify-end gap-2">
                    {editingId ? <Button type="button" variant="ghost" onClick={reset}>Cancelar</Button> : null}
                    <Button type="submit" disabled={lock.locked} title={lock.reason} isLoading={createStore.isPending || updateStore.isPending} leftIcon={editingId ? undefined : <Plus aria-hidden />}>
                        {editingId ? 'Guardar tienda' : 'Crear tienda'}
                    </Button>
                </div>
            </form>

            {isLoading ? (
                <Loading text="Cargando tiendas…" />
            ) : !data || data.length === 0 ? (
                <EmptyState title="Sin tiendas" description="Crea la primera tienda con el formulario de arriba." />
            ) : (
                <div className="space-y-2">
                    {data.map((s) => (
                        <Row
                            key={s.storeId}
                            title={s.name}
                            meta={s.code}
                            status={s.status}
                            actions={
                                <>
                                    <Button variant="outline" size="sm" onClick={() => { void getStore(retailerId, s.storeId).then((r) => { setViewRecord(asRecord(r)); }).catch((e: unknown) => toast.error(errMsg(e, 'Error'))); }}>Ver</Button>
                                    {canEditStore(s) ? (
                                        <>
                                            <Button variant="outline" size="sm" disabled={lock.locked} title={lock.reason} onClick={() => { startEdit(s); }}>Editar</Button>
                                            <Button variant="outline" size="sm" disabled={lock.locked} title={lock.reason} isLoading={changeStatus.isPending && changeStatus.variables.storeId === s.storeId} onClick={() => { changeStatus.mutate({ storeId: s.storeId, status: isActive(s.status) ? INACTIVE : ACTIVE }, { onError: (e) => toast.error(errMsg(e, 'Error')) }); }}>
                                                {isActive(s.status) ? 'Desactivar' : 'Activar'}
                                            </Button>
                                        </>
                                    ) : (
                                        <Badge variant="secondary">Administrada por el retailer</Badge>
                                    )}
                                </>
                            }
                        />
                    ))}
                </div>
            )}

            <EntityDetailModal title="Detalle de la tienda" record={viewRecord} onClose={() => { setViewRecord(null); }} />
        </div>
    );
}

// ── Usuarios retail ──────────────────────────────────────────────────────────

// Alta orquestada (C1/B4): el form pide email + rol + alcance (en vez de un cognitoSub pre-existente).
// `cognitoSub` se conserva en el estado solo para el flujo de EDICIÓN (poblar/mostrar el user existente).
const EMPTY_USER = { displayName: '', email: '', roleId: '', scopeKind: 'retailer', cognitoSub: '', homeStoreId: '', employmentType: EmploymentTypeEnum.EMPLOYEE as string, commissionTier: '', phone: '' };
// Alcance del rol en RBAC: todo el negocio (LEVEL_1 = retailer) o una tienda puntual (LEVEL_3 = store).
const SCOPE_OPTIONS: SelectOption[] = [
    { value: 'retailer', label: 'Todo el negocio' },
    { value: 'store', label: 'Solo la tienda base' },
];

function UsuariosTab({ retailerId }: { retailerId: string }) {
    const { data, isLoading } = useRetailUsers(retailerId);
    const { data: stores } = useStores(retailerId);
    const provisionUser = useProvisionRetailUser(retailerId);
    const updateUser = useUpdateRetailUser(retailerId);
    const changeStatus = useChangeRetailUserStatus(retailerId);
    const transfer = useTransferRetailUser(retailerId);
    const lock = useIntegrationLock();
    const [editingId, setEditingId] = useState<string | null>(null);
    const [form, setForm] = useState(EMPTY_USER);
    const [errors, setErrors] = useState<FieldErrors<'email' | 'phone'>>({});
    const [viewRecord, setViewRecord] = useState<Record<string, unknown> | null>(null);
    const [transferUser, setTransferUser] = useState<RetailUser | null>(null);
    const [walletUser, setWalletUser] = useState<RetailUser | null>(null);
    const canLinkWallet = usePermissions().hasPermission(WALLET_LINK_PERMISSION);

    const storeOptions = (stores ?? []).map((s) => ({ value: s.storeId, label: s.name }));
    // RetailUser NO guarda nombre — el nombre vive en RBAC (DEC-SKF1-005, match por cognitoSub). Se
    // muestra rol + tienda como etiqueta legible; el sub corto solo desambigua. (Ver el nombre real
    // requiere resolver cognitoSub→nombre vía RBAC — pendiente, frontera F0/F1.)
    const roleLabel = (v: string) => EMPLOYMENT_OPTIONS.find((o) => o.value === v)?.label ?? v;
    const storeName = (id: string) => storeOptions.find((o) => o.value === id)?.label ?? 'Sin tienda';
    const set = (k: keyof typeof EMPTY_USER) => (v: string) => {
        setForm((f) => ({ ...f, [k]: v }));
        setErrors((e) => ({ ...e, [k]: undefined }));
    };
    const reset = () => { setEditingId(null); setForm(EMPTY_USER); setErrors({}); };

    const submit = () => {
        if (editingId) {
            const checked = validateForm(retailUserEditSchema, { phone: form.phone });
            if (!checked.success) { setErrors(checked.errors); return; }
            const phone = checked.data.phone || undefined;
            updateUser.mutate(
                { retailUserId: editingId, body: { displayName: form.displayName || undefined, employmentType: form.employmentType as EmploymentTypeEnum, commissionTier: form.commissionTier, phone } },
                { onSuccess: () => { toast.success('Usuario actualizado'); reset(); }, onError: (e) => toast.error(errMsg(e, 'Error')) },
            );
            return;
        }
        const checked = validateForm(retailUserCreateSchema, { email: form.email, phone: form.phone });
        if (!checked.success) { setErrors(checked.errors); return; }
        const phone = checked.data.phone || undefined;
        // Alcance del rol → scope + scopeRef de RBAC: negocio = LEVEL_1 + retailerId · tienda = LEVEL_3 + homeStoreId.
        const store = form.scopeKind === 'store';
        const body: ProvisionRetailUserRequest = {
            email: checked.data.email,
            displayName: form.displayName || undefined,
            roleId: form.roleId,
            scope: store ? PermissionScope.LEVEL_3 : PermissionScope.LEVEL_1,
            scopeRef: store ? form.homeStoreId : retailerId,
            homeStoreId: form.homeStoreId,
            employmentType: form.employmentType as EmploymentTypeEnum,
            commissionTier: form.commissionTier,
            zoneId: null,
            phone,
        };
        provisionUser.mutate(body, {
            onSuccess: () => { toast.success('Usuario provisionado e invitado'); reset(); },
            onError: (e) => toast.error(errMsg(e, 'No se pudo provisionar el usuario')),
        });
    };

    return (
        <div className="space-y-4">
            <form onSubmit={(e) => { e.preventDefault(); submit(); }} className="space-y-3 rounded-lg border border-border bg-muted/30 p-3">
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                    <Input label="Nombre" value={form.displayName} onChange={(e) => { set('displayName')(e.target.value); }} hint="Cómo se muestra en la lista" placeholder="Ej. Juan Pérez" />
                    {editingId ? (
                        <Input label="cognitoSub" value={form.cognitoSub} hint="No editable" disabled />
                    ) : (
                        <>
                            <Input label="Email" type="email" value={form.email} onChange={(e) => { set('email')(e.target.value); }} hint="Se le invita a crear su cuenta" placeholder="usuario@negocio.com" error={errors.email} required />
                            <Input label="Rol (RBAC)" value={form.roleId} onChange={(e) => { set('roleId')(e.target.value); }} hint="Id del rol a asignar" placeholder="Ej. seller" required />
                            <Select label="Alcance del rol" value={form.scopeKind} onChange={set('scopeKind')} options={SCOPE_OPTIONS} required />
                        </>
                    )}
                    <Select label="Tienda base" value={form.homeStoreId} onChange={set('homeStoreId')} options={storeOptions} placeholder="Selecciona tienda…" disabled={!!editingId} required={!editingId} />
                    <Select label="Relación" value={form.employmentType} onChange={set('employmentType')} options={EMPLOYMENT_OPTIONS} required />
                    <Input label="Tier comisión" value={form.commissionTier} onChange={(e) => { set('commissionTier')(e.target.value); }} required />
                    <PhoneInput label="Teléfono" value={form.phone} onValueChange={set('phone')} error={errors.phone} />
                </div>
                <IntegrationLockNotice message="lockedProvision" />
                <div className="flex justify-end gap-2">
                    {editingId ? <Button type="button" variant="ghost" onClick={reset}>Cancelar</Button> : null}
                    <Button type="submit" isLoading={provisionUser.isPending || updateUser.isPending} leftIcon={editingId ? undefined : <Plus aria-hidden />}>
                        {editingId ? 'Guardar usuario' : 'Crear usuario'}
                    </Button>
                </div>
            </form>

            {isLoading ? (
                <Loading text="Cargando usuarios…" />
            ) : !data || data.length === 0 ? (
                <EmptyState title="Sin usuarios" description="Crea el primer usuario con el formulario de arriba." />
            ) : (
                <div className="space-y-2">
                    {data.map((u) => (
                        <Row
                            key={u.retailUserId}
                            title={u.displayName ?? `${roleLabel(u.employmentType)} · ${storeName(u.homeStoreId)}`}
                            meta={`${u.displayName ? `${roleLabel(u.employmentType)} · ${storeName(u.homeStoreId)}` : `#${u.cognitoSub.slice(0, 8)}`} · ${walletBadgeText(u)}`}
                            status={u.status}
                            actions={
                                <>
                                    <Button variant="outline" size="sm" onClick={() => { void getRetailUser(retailerId, u.retailUserId).then((r) => { setViewRecord(asRecord(r)); }).catch((e: unknown) => toast.error(errMsg(e, 'Error'))); }}>Ver</Button>
                                    <Button variant="outline" size="sm" disabled={lock.locked} title={lock.reason} onClick={() => { setEditingId(u.retailUserId); setForm({ ...EMPTY_USER, displayName: u.displayName ?? '', cognitoSub: u.cognitoSub, homeStoreId: u.homeStoreId, employmentType: u.employmentType, commissionTier: u.commissionTier, phone: fromStoredPhone(u.phone) }); }}>Editar</Button>
                                    <Button variant="outline" size="sm" disabled={lock.locked} title={lock.reason} onClick={() => { setTransferUser(u); }}>Transferir</Button>
                                    {canLinkWallet ? <Button variant="outline" size="sm" onClick={() => { setWalletUser(u); }}>Wallet</Button> : null}
                                    <Button variant="outline" size="sm" disabled={lock.locked} title={lock.reason} isLoading={changeStatus.isPending && changeStatus.variables.retailUserId === u.retailUserId} onClick={() => { changeStatus.mutate({ retailUserId: u.retailUserId, status: isActive(u.status) ? INACTIVE : ACTIVE }, { onError: (e) => toast.error(errMsg(e, 'Error')) }); }}>
                                        {isActive(u.status) ? 'Desactivar' : 'Activar'}
                                    </Button>
                                </>
                            }
                        />
                    ))}
                </div>
            )}

            <EntityDetailModal title="Detalle del usuario" record={viewRecord} onClose={() => { setViewRecord(null); }} />
            {walletUser ? <SellerWalletModal retailerId={retailerId} user={walletUser} name={walletUser.displayName ?? `#${walletUser.cognitoSub.slice(0, 8)}`} onClose={() => { setWalletUser(null); }} /> : null}
            <TransferUserModal
                key={transferUser?.retailUserId ?? 'no-transfer'}
                user={transferUser}
                storeOptions={storeOptions}
                isPending={transfer.isPending}
                onClose={() => { setTransferUser(null); }}
                onConfirm={(homeStoreId) => {
                    if (!transferUser) return;
                    transfer.mutate(
                        { retailUserId: transferUser.retailUserId, homeStoreId },
                        { onSuccess: () => { toast.success('Usuario transferido'); setTransferUser(null); }, onError: (e) => toast.error(errMsg(e, 'Error')) },
                    );
                }}
            />
        </div>
    );
}

function TransferUserModal({
    user,
    storeOptions,
    isPending,
    onClose,
    onConfirm,
}: {
    user: RetailUser | null;
    storeOptions: SelectOption[];
    isPending: boolean;
    onClose: () => void;
    onConfirm: (homeStoreId: string) => void;
}) {
    const [storeId, setStoreId] = useState('');
    return (
        <Modal isOpen={!!user} onClose={onClose} title="Transferir usuario" size="sm">
            <div className="space-y-4">
                <Select label="Nueva tienda base" value={storeId} onChange={setStoreId} options={storeOptions} placeholder="Selecciona tienda…" />
                <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={onClose}>Cancelar</Button>
                    <Button isLoading={isPending} disabled={!storeId} onClick={() => { onConfirm(storeId); }}>Transferir</Button>
                </div>
            </div>
        </Modal>
    );
}

// ── Jerarquía ────────────────────────────────────────────────────────────────

// La jerarquía real que devuelve el backend es el árbol de RETAILERS: { retailer, children[] }
// (recursivo) — negocio → subdistribuidores/franquicias → sus hijos. NO zonas/tiendas.
// ⚠️ El tipo `RetailerHierarchy` del client dice zones/unzonedStores: está desactualizado (TD).
interface HierNode {
    retailer?: { retailerId?: string; name?: string; type?: string };
    children?: HierNode[];
}

const HIER_TYPE_LABEL: Record<string, string> = {
    CHAIN: 'Cadena',
    SUBDISTRIBUTOR: 'Subdistribuidor',
    FRANCHISE: 'Franquicia',
};

function HierNodeView({ node, depth }: { node: HierNode; depth: number }) {
    const children = node.children ?? [];
    return (
        <div className={depth > 0 ? 'ml-3 border-l border-border pl-3' : ''}>
            <div className="flex flex-wrap items-center gap-2 py-0.5">
                <span className="font-medium">{node.retailer?.name ?? '—'}</span>
                {node.retailer?.type ? (
                    <Badge variant="secondary">{HIER_TYPE_LABEL[node.retailer.type] ?? node.retailer.type}</Badge>
                ) : null}
            </div>
            {children.map((c, i) => (
                <HierNodeView key={c.retailer?.retailerId ?? String(i)} node={c} depth={depth + 1} />
            ))}
        </div>
    );
}

function JerarquiaTab({ retailerId }: { retailerId: string }) {
    const { data, isLoading, isError, error } = useRetailerHierarchy(retailerId, true);

    if (isLoading) return <Loading text="Cargando jerarquía…" />;
    if (isError) return <EmptyState title="No se pudo cargar la jerarquía" description={errMsg(error, 'Error')} />;
    if (!data) return <EmptyState title="Sin datos" description="No hay jerarquía para mostrar." />;

    const node = data as unknown as HierNode;
    // Si la forma no calza (sin `retailer`), se muestra el JSON crudo sin romper.
    if (!node.retailer) {
        return (
            <pre className="max-h-96 overflow-auto rounded-md border border-border bg-muted/40 p-3 text-[11px] leading-tight">
                {JSON.stringify(data, null, 2)}
            </pre>
        );
    }

    const hasChildren = (node.children ?? []).length > 0;
    return (
        <div className="space-y-1 text-[13px]">
            <p className="mb-2 text-[12px] text-muted-foreground">
                Árbol del negocio y sus subdistribuidores / franquicias.
            </p>
            <HierNodeView node={node} depth={0} />
            {!hasChildren ? (
                <p className="pt-2 text-[12px] text-muted-foreground">
                    Este negocio aún no tiene subdistribuidores ni franquicias colgando.
                </p>
            ) : null}
        </div>
    );
}
